import { useEffect, useState } from "react";
import { adminPost } from "@/lib/kbAdminClient";
import { SectionCard } from "./SectionCard";

export type KbChangeProposalRow = {
  id: string;
  organization_id: string;
  proposal_kind?: string | null;
  target_slug: string | null;
  base_version: number;
  title: string;
  body_markdown: string;
  audience: string;
  doc_type?: string | null;
  status: string;
  submitted_by_name: string | null;
  review_note: string | null;
  reviewed_by_name: string | null;
  reviewed_at: string | null;
  created_at: string;
  context_query?: string | null;
  context_ai_answer?: string | null;
  context_sources?: unknown;
  review_summary?: string | null;
};

type Props = {
  editorName: string;
  onNote: (title: string, text: string, tone: "success" | "error" | "info") => void;
  onPendingCountChange?: (n: number) => void;
};

export function KbChangeProposalsSection(props: Props) {
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [proposals, setProposals] = useState<KbChangeProposalRow[]>([]);
  const [rejectNote, setRejectNote] = useState<Record<string, string>>({});

  async function load() {
    setLoading(true);
    try {
      const response = await adminPost<{ ok: true; proposals: KbChangeProposalRow[] }>(
        "/api/kb/admin/listProposals",
        { status: "pending", limit: 100 }
      );
      if (!response.ok) {
        if (response.status === 503) {
          setProposals([]);
          props.onPendingCountChange?.(0);
          return;
        }
        props.onNote("Could not load proposals", response.error, "error");
        setProposals([]);
        return;
      }
      const list = response.data.proposals || [];
      setProposals(list);
      props.onPendingCountChange?.(list.length);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // Intentionally once on mount; use Refresh or post-review reload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function review(
    id: string,
    decision: "approve" | "reject",
    policyConfirmed = false,
    proposalKind?: string | null
  ) {
    setWorkingId(id);
    try {
      const response = await adminPost<{ ok: true }>("/api/kb/admin/reviewProposal", {
        proposalId: id,
        decision,
        reviewNote: rejectNote[id]?.trim() || undefined,
        reviewedBy: props.editorName,
        ...(policyConfirmed ? { confirmPolicyOverwrite: true } : {}),
      });
      const policyMsg =
        proposalKind === "general_info"
          ? "This proposal publishes new policy-classified KB content. Continue?"
          : "The live article is policy-classified. Approving will publish this trainer draft over the current policy text. Continue?";
      if (
        !response.ok &&
        response.code === "POLICY_OVERWRITE_CONFIRM" &&
        decision === "approve" &&
        window.confirm(policyMsg)
      ) {
        return review(id, decision, true, proposalKind);
      }
      if (!response.ok) {
        props.onNote(decision === "approve" ? "Approve failed" : "Reject failed", response.error, "error");
        return;
      }
      props.onNote(
        decision === "approve" ? "Change approved" : "Proposal rejected",
        decision === "approve"
          ? "The live article was updated and re-embedded."
          : "The trainer can submit a new proposal if needed.",
        "success"
      );
      await load();
    } finally {
      setWorkingId(null);
    }
  }

  return (
    <SectionCard
      title="Change proposals"
      description="Trainer-submitted updates: edits to existing org articles, or new general articles from Training (with Q&A context). Approve publishes and re-embeds; reject records an audit note."
      rightSlot={
        <button
          type="button"
          className="text-xs rounded-lg border border-gray-300 bg-white px-2 py-1 text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
          onClick={() => void load()}
          disabled={loading}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      }
    >
      {proposals.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">{loading ? "Loading…" : "No pending proposals."}</p>
      ) : (
        <ul className="space-y-4">
          {proposals.map((p) => (
            <li key={p.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-2 dark:border-zinc-700 dark:bg-zinc-900/40">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{p.title}</div>
                    {(p.proposal_kind || "article_edit") === "general_info" ? (
                      <span className="text-[10px] font-semibold uppercase tracking-wide rounded-full bg-indigo-100 text-indigo-800 px-2 py-0.5 border border-indigo-200 dark:bg-indigo-950/60 dark:text-indigo-200 dark:border-indigo-700">
                        New article
                      </span>
                    ) : null}
                  </div>
                  {p.review_summary?.trim() ? (
                    <p className="text-xs text-gray-700 mt-2 leading-snug pl-2 border-l-2 border-amber-300 dark:text-zinc-300 dark:border-amber-700">
                      <span className="font-semibold text-amber-900 dark:text-amber-200">Reviewer summary: </span>
                      {p.review_summary.trim()}
                    </p>
                  ) : null}
                  <div className="text-xs text-gray-500 mt-1 dark:text-zinc-400">
                    {(p.proposal_kind || "article_edit") === "general_info" ? (
                      <>
                        slug assigned on approve · from {p.submitted_by_name || "unknown"}
                      </>
                    ) : (
                      <>
                        slug <span className="font-mono text-gray-700 dark:text-zinc-300">{p.target_slug}</span> · base v{p.base_version}{" "}
                        · from {p.submitted_by_name || "unknown"}
                      </>
                    )}
                  </div>
                  <div className="text-xs text-gray-400 dark:text-zinc-500">{new Date(p.created_at).toLocaleString()}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
                    disabled={workingId === p.id}
                    onClick={() => void review(p.id, "approve", false, p.proposal_kind)}
                  >
                    {workingId === p.id ? "…" : "Approve"}
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-800 disabled:opacity-50 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300"
                    disabled={workingId === p.id}
                    onClick={() => void review(p.id, "reject", false, p.proposal_kind)}
                  >
                    Reject
                  </button>
                </div>
              </div>
              {(p.proposal_kind || "article_edit") === "general_info" &&
              (p.context_query || p.context_ai_answer || p.context_sources) ? (
                <details className="text-xs border border-indigo-100 rounded-lg bg-indigo-50/50 p-2 dark:border-indigo-800 dark:bg-indigo-950/35">
                  <summary className="cursor-pointer font-medium text-indigo-800 dark:text-indigo-200">Training context (review)</summary>
                  {p.context_query ? (
                    <div className="mt-2">
                      <div className="text-[10px] uppercase text-indigo-600 dark:text-indigo-400">Question</div>
                      <div className="text-gray-800 whitespace-pre-wrap dark:text-zinc-200">{p.context_query}</div>
                    </div>
                  ) : null}
                  {p.context_ai_answer ? (
                    <div className="mt-2">
                      <div className="text-[10px] uppercase text-indigo-600 dark:text-indigo-400">AI answer shown</div>
                      <div className="text-gray-800 whitespace-pre-wrap dark:text-zinc-200">{p.context_ai_answer}</div>
                    </div>
                  ) : null}
                  {Array.isArray(p.context_sources) && p.context_sources.length > 0 ? (
                    <div className="mt-2">
                      <div className="text-[10px] uppercase text-indigo-600 dark:text-indigo-400">Sources</div>
                      <ul className="list-disc pl-4 text-gray-700 dark:text-zinc-300">
                        {(p.context_sources as { title?: string }[]).slice(0, 12).map((s, i) => (
                          <li key={i}>{s.title || "(untitled)"}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </details>
              ) : null}
              <label className="block text-xs text-gray-600 dark:text-zinc-400">
                Optional note (shown on reject, stored on approve)
                <input
                  className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
                  value={rejectNote[p.id] || ""}
                  onChange={(e) => setRejectNote((prev) => ({ ...prev, [p.id]: e.target.value }))}
                  placeholder="Reason for rejection or approval context"
                />
              </label>
              <details className="text-xs">
                <summary className="cursor-pointer text-indigo-700 font-medium dark:text-indigo-300">Preview body</summary>
                <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-white border border-gray-100 p-2 text-gray-800 dark:bg-zinc-900/60 dark:border-zinc-700 dark:text-zinc-200">
                  {p.body_markdown}
                </pre>
              </details>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  );
}
